import ActionHeader from './ActionHeader'
import useActionLabel from '../../useActionLabel'
import PopoverLink from 'Shared/components/PopoverLink/PopoverLink'
import './ActionStats.scoped.scss'

function resolveTextContent(textContent, defaultLanguage) {
  if (!textContent) return {}
  const lang = defaultLanguage || 'en'
  return textContent[lang] || textContent[Object.keys(textContent)[0]] || {}
}

function SharePreview({ textContent }) {
  const title = textContent?.shareTitle || 'Share Title'
  const description = textContent?.shareDescription || 'Share description'
  const imageUrl = textContent?.shareImageUrl
  const shareText = textContent?.shareReason

  return (
    <div className="share-preview-compact">
      <div className="share-preview-wa">
        {shareText && (
          <div className="share-preview-bubble">
            <p className="share-preview-text">{shareText}</p>
          </div>
        )}
        <div className="share-preview-bubble">
          <div className="share-preview-link-card">
            {imageUrl && <img src={imageUrl} alt="Share preview" className="share-preview-image" />}
            <div className="share-preview-link-info">
              <span className="share-preview-domain">yoursite.com</span>
              <strong className="share-preview-link-title">{title}</strong>
              <p className="share-preview-link-desc">{description}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ShareVariantStats({ shareContentTest }) {
  const defaultLanguage = useSel(s => s.flow?.defaultLanguage)
  const variants = shareContentTest.contentVariants?.filter(v => v.active !== false) || []
  const stats = shareContentTest.stats?.variants || {}
  const proportions = shareContentTest.stats?.proportions || {}
  const isMultiVariant = variants.length > 1

  const idOrderedVariants = _.orderBy(variants, 'id')

  // Assign stable letters based on ID order
  const letterByVariantId = {}
  idOrderedVariants.forEach((v, i) => {
    letterByVariantId[v.id] = String.fromCharCode(65 + i)
  })

  const clickRates = idOrderedVariants.map(v => {
    const s = stats[v.id]
    return s?.trialCount > 0 ? (s.uniqueClickCount / s.trialCount) : 0
  })
  const minClickRate = Math.min(...clickRates)
  const maxClickRate = Math.max(...clickRates)

  const objectiveRates = idOrderedVariants.map(v => {
    const s = stats[v.id]
    return s?.trialCount > 0 ? (s.uniqueObjectiveCount / s.trialCount) : 0
  })
  const maxObjectiveRate = Math.max(...objectiveRates)

  const hasObjectives = idOrderedVariants.some(v => {
    const s = stats[v.id]
    return s?.uniqueObjectiveCount > 0
  })

  // Sort by click rate descending for display
  const orderedVariants = _.orderBy(idOrderedVariants, v => {
    const s = stats[v.id]
    return s?.trialCount > 0 ? (s.uniqueClickCount / s.trialCount) : 0
  }, 'desc')

  function formatRate(numerator, denominator) {
    if (!denominator || denominator === 0) return '0%'
    return `${Math.round((numerator / denominator) * 100)}%`
  }

  function formatImprovement(variantStats) {
    if (!variantStats?.trialCount || variantStats.trialCount === 0 || minClickRate === 0) return '-'
    const rate = variantStats.uniqueClickCount / variantStats.trialCount
    if (rate === minClickRate) return '-'
    const improvement = ((rate - minClickRate) / minClickRate) * 100
    return `+${Math.round(improvement)}%`
  }

  return (
    <div className="share-variant-list">
      {orderedVariants.map((variant) => {
        const variantStats = stats[variant.id] || {}
        const letter = letterByVariantId[variant.id]
        const trialCount = variantStats.trialCount || 0
        const clickRate = trialCount > 0 ? (variantStats.uniqueClickCount || 0) / trialCount : 0
        const objectiveRate = trialCount > 0 ? (variantStats.uniqueObjectiveCount || 0) / trialCount : 0
        const allocation = proportions[variant.id]
        const textContent = resolveTextContent(variant.content?.textContent, defaultLanguage)

        return (
          <div key={variant.id} className="variant-card">
            <div className="variant-card-body">
              <SharePreview textContent={textContent} />
              <div className="variant-card-right">
                <div className="variant-card-title">
                  {isMultiVariant && <div className="variant-letter">{letter}</div>}
                  <span className="variant-info"><strong>Share:</strong> {textContent.shareTitle || textContent.shareDescription || `Variant ${letter}`}</span>
                </div>
                <div className="variant-card-stats">
                  <div className="stat-item">
                    <span className="stat-header">SHARES</span>
                    <span className="stat-value">{trialCount.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-header">CLICK RATE</span>
                    <span className={`stat-value ${clickRate > 0 && clickRate === maxClickRate ? 'stat-highlight' : ''}`}>
                      {formatRate(variantStats.uniqueClickCount, trialCount)}
                    </span>
                    <span className="stat-subvalue">{(variantStats.uniqueClickCount || 0).toLocaleString()} clicks</span>
                  </div>
                  {hasObjectives && (
                    <div className="stat-item">
                      <span className="stat-header">OBJECTIVE RATE</span>
                      <span className={`stat-value ${objectiveRate > 0 && objectiveRate === maxObjectiveRate ? 'stat-highlight' : ''}`}>
                        {formatRate(variantStats.uniqueObjectiveCount, trialCount)}
                      </span>
                      <span className="stat-subvalue">{(variantStats.uniqueObjectiveCount || 0).toLocaleString()} objectives</span>
                    </div>
                  )}
                  {isMultiVariant && (
                    <div className="stat-item">
                      <span className="stat-header">
                        <PopoverLink content={() => <span>The percentage increase in clicks vs the bottom version</span>} noQuestionIcon className="stat-header-popover">
                          IMPROVEMENT <SvgIconsCircleInfo width={14} height={14} />
                        </PopoverLink>
                      </span>
                      <span className={`stat-value ${clickRate > 0 && clickRate === maxClickRate ? 'stat-highlight' : ''}`}>
                        {formatImprovement(variantStats)}
                      </span>
                    </div>
                  )}
                  {isMultiVariant && (
                    <div className="stat-item">
                      <span className="stat-header">
                        <PopoverLink content={() => <span>The percentage of people sharing who will be given this version. As Movement collects more data, the versions with the best performance are automatically shared more often.</span>} noQuestionIcon className="stat-header-popover">
                          ALLOCATION <SvgIconsCircleInfo width={14} height={14} />
                        </PopoverLink>
                      </span>
                      <span className="stat-value">
                        {allocation != null ? `${Math.round(allocation * 100)}%` : '-'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function Share({ flowId, actionId, removed }) {
  const stats = useSel(s => s.flow)?.stats
  const shareContentTest = useSel(s => s.flow?.shareContentTest)
  const completed = stats?.steps?.find(step => step.kind === 'share')?.completed || 0
  const label = useActionLabel('share')

  const featureEnabled = window.ENV?.FEATURES?.['share_content_variants'] || false
  const hasStats = featureEnabled && shareContentTest?.stats?.variants && Object.keys(shareContentTest.stats.variants).length > 0

  return (
    <div className="flow-action-stats">
      <div className='box margin-top double' id='share'>
        <ActionHeader flowId={flowId} actionId={actionId} title={`${label}${removed ? ' (removed)' : ''}`} />
        <p className="bold">{`${completed} ${completed === 1 ? 'person has' : 'people have'} shared this page`}</p>
        {hasStats && <ShareVariantStats shareContentTest={shareContentTest} />}
      </div>
    </div>
  )
}
